import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { formatTime } from '../../lib/dateFmt';
import {
  Search,
  CheckCircle2,
  AlertCircle,
  Camera,
  ChevronDown,
  ChevronRight,
  Flag,
  ClipboardList,
  MessageSquare,
  Undo2,
  Users,
  UserPlus,
} from 'lucide-react';
import type { MobilisationEvent } from '@hcc/types';
import { useCurrentUser } from '../../hooks/useAuth';
import { usePeopleList, useAppendEvent } from '../../hooks/usePeople';
import { useIntentKey } from '../../hooks/useIntentKey';
import { RaiseFlagModal } from '../../components/person/RaiseFlagModal';
import { FlagsButton } from '../../components/person/FlagsButton';
import { PersonAvatar } from '../../components/person/PersonAvatar';
import { PhotoCaptureModal } from '../../components/person/PhotoCaptureModal';
import { PersonNotesDialog } from '../../components/person/PersonNotesDialog';
import { PersonTasksModal } from '../../components/person/PersonTasksModal';
import { Button } from '../../components/ui/Button';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { Hero } from '../../components/ui/Hero';
import { MetricBlock } from '../../components/ui/MetricBlock';
import { Pill } from '../../components/ui/Pill';
import { ProgressBar } from '../../components/ui/ProgressBar';
import { SkeletonList } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';
import { api, ApiError } from '../../lib/api';

/**
 * Reception desk view. Search-as-you-type over attendees, one-tap
 * check-in. CLAUDE.md 6.2: "Searchable list (fuzzy match on name),
 * one-tap check-in button."
 *
 * Visual brief: this and StationView are the highest-volume staff
 * surfaces. Big tap targets, clear empty state, immediate toast
 * feedback on check-in.
 */
export function CheckIn() {
  const { data: user, isLoading: meLoading } = useCurrentUser();
  const [q, setQ] = useState('');
  const list = usePeopleList(q);
  const [showArrived, setShowArrived] = useState(false);
  // Photo modal is hoisted to the parent: a successful check-in
  // refetches and re-filters the row out of "to check in", which
  // would unmount any modal state owned by the row itself. Holding it
  // here keeps the prompt visible regardless of where the row lands.
  const [photoFor, setPhotoFor] = useState<{ id: string; name: string } | null>(
    null,
  );

  const { notArrived, arrived, counts } = useMemo(() => {
    const all = list.data ?? [];
    const arr = all.filter((p) => p.arrived);
    return {
      notArrived: all.filter((p) => !p.arrived),
      arrived: arr,
      counts: { total: all.length, arrived: arr.length },
    };
  }, [list.data]);

  // When searching, auto-expand the "Checked in" section so matches
  // there aren't hidden. Collapse again once the search clears.
  const searching = q.trim().length > 0;
  const arrivedExpanded = searching || showArrived;

  if (meLoading) return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  if (!user) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Please <Link to="/login" className="underline">sign in</Link>.
      </main>
    );
  }
  if (user.roles.length === 0) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Your account has no permissions yet. Ask an admin.
      </main>
    );
  }

  const total = counts.total;
  const yetToArrive = Math.max(0, total - counts.arrived);

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-10">
        <Hero
          eyebrow={
            <>
              <ClipboardList className="h-3 w-3" />
              <span>Reception</span>
            </>
          }
          title="Welcome to mobilisation day"
          description="Tap a name to check someone in. New arrivals broadcast to every station instantly, so the right team always knows who's on site."
          trailing={
            list.data && list.data.length > 0 ? (
              <div className="grid w-full max-w-sm grid-cols-2 gap-3 sm:w-auto">
                <MetricBlock
                  label="On site"
                  value={
                    <span>
                      {counts.arrived}
                      <span className="text-fg-dim">/{total}</span>
                    </span>
                  }
                />
                <MetricBlock
                  label="Yet to arrive"
                  value={yetToArrive}
                  hint="across all roles"
                />
              </div>
            ) : null
          }
        >
          {list.data && list.data.length > 0 ? (
            <ProgressBar
              value={counts.arrived}
              total={total}
              ariaLabel="Arrival progress"
            />
          ) : null}
        </Hero>

        <div className="relative mt-6">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-fg-dim" />
          <input
            type="search"
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Type a name or email..."
            className="block h-12 w-full rounded-token-lg border border-line-strong/20 bg-surface pl-11 pr-3 text-base text-fg shadow-token-sm transition-colors focus:border-accent focus:outline-none focus:ring-2 focus:ring-ring/30"
          />
        </div>

        {list.isLoading ? (
          <div className="mt-4 overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-sm">
            <SkeletonList rows={6} />
          </div>
        ) : list.error ? (
          <div className="mt-4 overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-sm">
            <EmptyState
              icon={<AlertCircle className="h-6 w-6" />}
              title="Couldn't load attendees"
              description={(list.error as Error).message}
            />
          </div>
        ) : !list.data || list.data.length === 0 ? (
          <div className="mt-4 overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-sm">
            {q ? (
              <EmptyState
                icon={<Search className="h-6 w-6" />}
                title="No matches"
                description={`Nothing for "${q}". Check the spelling or clear the search.`}
              />
            ) : (
              <EmptyState
                icon={<Users className="h-6 w-6" />}
                title="No attendees yet"
                description="Import a CSV of attendees from the admin area to get started."
                action={
                  <Link to="/admin/import-attendees">
                    <Button variant="primary" iconLeft={<UserPlus className="h-4 w-4" />}>
                      Import attendees
                    </Button>
                  </Link>
                }
              />
            )}
          </div>
        ) : (
          <>
            {/* Primary work list — people still to check in. */}
            <section
              aria-label="To check in"
              className="mt-6 overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-sm"
            >
              <header className="flex items-center justify-between border-b border-divider/10 px-5 py-4">
                <div className="flex items-center gap-2.5">
                  <span
                    aria-hidden
                    className="inline-flex h-8 w-8 items-center justify-center rounded-token-sm bg-accent/10 text-accent"
                  >
                    <ClipboardList className="h-4 w-4" />
                  </span>
                  <h2 className="text-base font-semibold text-fg">
                    Still to check in
                  </h2>
                  <Pill tone="accent">{notArrived.length}</Pill>
                </div>
              </header>
              {notArrived.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-fg-muted">
                  {searching
                    ? 'No matches still to check in.'
                    : 'Everyone has arrived. Nice work.'}
                </div>
              ) : (
                <ul className="divide-y divide-divider/10">
                  {notArrived.map((p) => (
                    <PersonRow
                      key={p.id}
                      person={p}
                      onOpenPhoto={() =>
                        setPhotoFor({ id: p.id, name: p.full_name })
                      }
                    />
                  ))}
                </ul>
              )}
            </section>

            {/* Already-arrived list — collapsed by default so the main
                work list stays uncluttered. Auto-expands while searching. */}
            <section
              aria-label="Checked in"
              className="mt-5 overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-sm"
            >
              <button
                type="button"
                onClick={() => setShowArrived((v) => !v)}
                aria-expanded={arrivedExpanded}
                className={`flex w-full items-center justify-between gap-3 px-5 py-4 text-left transition-colors ${
                  arrivedExpanded
                    ? 'border-b border-divider/10 bg-success-soft/10'
                    : 'hover:bg-surface-2'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  {arrivedExpanded ? (
                    <ChevronDown className="h-4 w-4 text-success-soft-fg" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-success-soft-fg" />
                  )}
                  <span
                    aria-hidden
                    className="inline-flex h-8 w-8 items-center justify-center rounded-token-sm bg-success-soft/15 text-success-soft-fg"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                  </span>
                  <h2 className="text-base font-semibold text-fg">
                    Checked in
                  </h2>
                  <Pill tone="success">{arrived.length}</Pill>
                </div>
                <span className="text-xs text-fg-muted">
                  {arrivedExpanded
                    ? searching
                      ? 'showing matches'
                      : 'tap to hide'
                    : 'tap to show'}
                </span>
              </button>
              {arrivedExpanded ? (
                arrived.length === 0 ? (
                  <div className="px-4 py-8 text-center text-sm text-fg-muted">
                    {searching
                      ? 'No matches in the checked-in list.'
                      : 'No one has arrived yet.'}
                  </div>
                ) : (
                  <ul className="divide-y divide-divider/10">
                    {arrived.map((p) => (
                      <PersonRow
                        key={p.id}
                        person={p}
                        onOpenPhoto={() =>
                          setPhotoFor({ id: p.id, name: p.full_name })
                        }
                      />
                    ))}
                  </ul>
                )
              ) : null}
            </section>
          </>
        )}
      </div>
      {photoFor ? (
        <PhotoFlow
          personId={photoFor.id}
          personName={photoFor.name}
          onClose={() => setPhotoFor(null)}
        />
      ) : null}
    </main>
  );
}

function PhotoFlow({
  personId,
  personName,
  onClose,
}: {
  personId: string;
  personName: string;
  onClose: () => void;
}) {
  const append = useAppendEvent(personId);
  const intent = useIntentKey();
  async function save(dataUrl: string) {
    await append.mutateAsync({
      event_type: 'PhotoCaptured',
      payload: { captured_at: 'reception' },
      photo_jpeg: dataUrl,
      client_event_id: intent.key,
    } as Parameters<typeof append.mutateAsync>[0]);
    intent.renew();
    toast.success(`Photo saved for ${personName}`);
    onClose();
  }
  return (
    <PhotoCaptureModal
      open
      personName={personName}
      onClose={onClose}
      onSave={save}
    />
  );
}

function PersonRow({
  person,
  onOpenPhoto,
}: {
  person: import('../../hooks/usePeople').PersonApi;
  onOpenPhoto: () => void;
}) {
  const append = useAppendEvent(person.id);
  const arrivedIntent = useIntentKey();
  const flagIntent = useIntentKey();
  const [raising, setRaising] = useState(false);
  const [confirmUndo, setConfirmUndo] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [tasksOpen, setTasksOpen] = useState(false);

  async function checkIn() {
    try {
      await append.mutateAsync({
        event_type: 'PersonArrived',
        payload: { via: 'reception' },
        client_event_id: arrivedIntent.key,
      } as Parameters<typeof append.mutateAsync>[0]);
      arrivedIntent.renew();
      toast.success(`${person.full_name} checked in`);
      // Photo flow lives in the parent — the row may unmount after the
      // refetch moves the person between sections.
      onOpenPhoto();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : 'Check-in failed. Try again.',
      );
    }
  }

  async function performUndo(reason?: string) {
    try {
      // Walk the person's event chain to find the most recent
      // PersonArrived that hasn't already been undone, then append a
      // corrective ArrivalUndone referencing it.
      const { events } = await api<{ events: MobilisationEvent[] }>(
        `/api/people/${person.id}/events`,
      );
      const undoneIds = new Set(
        events
          .filter((e) => e.event_type === 'ArrivalUndone')
          .map((e) => (e.payload as { arrival_event_id?: string }).arrival_event_id)
          .filter((x): x is string => Boolean(x)),
      );
      const target = [...events]
        .reverse()
        .find((e) => e.event_type === 'PersonArrived' && !undoneIds.has(e.id));
      if (!target) {
        toast.error('No live arrival to undo.');
        return;
      }
      await append.mutateAsync({
        event_type: 'ArrivalUndone',
        payload: reason
          ? { arrival_event_id: target.id, reason }
          : { arrival_event_id: target.id },
      } as Parameters<typeof append.mutateAsync>[0]);
      toast.success(`Undid check-in for ${person.full_name}`);
      setConfirmUndo(false);
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : 'Undo failed. Try again.',
      );
    }
  }

  async function raiseFlag(input: { reason: string }) {
    try {
      await append.mutateAsync({
        event_type: 'FlagRaised',
        payload: input,
        client_event_id: flagIntent.key,
      } as Parameters<typeof append.mutateAsync>[0]);
      flagIntent.renew();
      toast.success(`Flag raised on ${person.full_name}`);
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : 'Could not raise flag',
      );
      throw err; // let the modal stay open so the user can retry
    }
  }

  const photoLabel = person.current_photo_s3_key ? 'Retake photo' : 'Take photo';
  return (
    <li className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        {person.arrived ? (
          <PersonAvatar
            fullName={person.full_name}
            photoKey={person.current_photo_s3_key}
            size="md"
          />
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 text-base font-semibold text-fg">
            <span className="truncate">{person.full_name}</span>
            <FlagsButton
              personId={person.id}
              personName={person.full_name}
              count={person.active_flags ?? 0}
            />
          </div>
          <div className="mt-0.5 text-xs text-fg-muted">
            {person.position ?? 'Position unknown'}
            {person.email ? ` · ${person.email}` : ''}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button
          variant="ghost"
          size="sm"
          iconLeft={<MessageSquare className="h-3.5 w-3.5" />}
          onClick={() => setNotesOpen(true)}
        >
          Notes
        </Button>
        {(person.outstanding_tasks ?? 0) > 0 ? (
          // Matches the Coordinator view's TasksCell: an orange warning
          // pill with the count, so the receptionist spots outstanding
          // tasks at a glance. Click opens the same modal to view / add.
          <button
            type="button"
            onClick={() => setTasksOpen(true)}
            className="inline-flex"
            title={`${person.outstanding_tasks} outstanding — click to view or add`}
            aria-label={`${person.outstanding_tasks} outstanding task${
              person.outstanding_tasks === 1 ? '' : 's'
            } for ${person.full_name}`}
          >
            <Pill tone="warning" icon={<ClipboardList className="h-3.5 w-3.5" />}>
              {person.outstanding_tasks}
            </Pill>
          </button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            iconLeft={<ClipboardList className="h-3.5 w-3.5" />}
            onClick={() => setTasksOpen(true)}
            title="View or add tasks"
            aria-label={`Tasks for ${person.full_name}`}
          >
            Tasks
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          iconLeft={<Flag className="h-3.5 w-3.5" />}
          onClick={() => setRaising(true)}
        >
          Flag
        </Button>
        {person.arrived ? (
          <>
            <Button
              variant="subtle"
              size="sm"
              iconLeft={<Camera className="h-3.5 w-3.5" />}
              onClick={onOpenPhoto}
              title={photoLabel}
              aria-label={`${photoLabel} for ${person.full_name}`}
            >
              {photoLabel}
            </Button>
            <Pill
              tone="success"
              icon={<CheckCircle2 className="h-3.5 w-3.5" />}
            >
              Arrived
              {person.arrived_at
                ? ` ${formatTime(person.arrived_at)}`
                : ''}
            </Pill>
            <Button
              variant="warning"
              size="sm"
              iconLeft={<Undo2 className="h-3.5 w-3.5" />}
              onClick={() => setConfirmUndo(true)}
              title="Undo check-in"
              aria-label={`Undo check-in for ${person.full_name}`}
            >
              Undo
            </Button>
          </>
        ) : (
          <Button
            variant="success"
            size="md"
            iconLeft={<CheckCircle2 className="h-4 w-4" />}
            loading={append.isPending}
            onClick={checkIn}
          >
            Check in
          </Button>
        )}
      </div>
      <RaiseFlagModal
        open={raising}
        personName={person.full_name}
        onClose={() => setRaising(false)}
        onSubmit={raiseFlag}
      />
      <PersonNotesDialog
        open={notesOpen}
        personId={person.id}
        personName={person.full_name}
        onClose={() => setNotesOpen(false)}
      />
      <PersonTasksModal
        open={tasksOpen}
        personId={person.id}
        personName={person.full_name}
        onClose={() => setTasksOpen(false)}
      />
      <ConfirmModal
        open={confirmUndo}
        tone="warning"
        title={`Undo check-in for ${person.full_name}?`}
        message={
          <>
            The arrival event stays in the audit trail; the person will
            simply read as <strong>not on site</strong> again. Use this
            if you tapped the wrong row by mistake.
          </>
        }
        confirmLabel="Undo check-in"
        cancelLabel="Keep checked in"
        reasonLabel="Reason"
        reasonPlaceholder="e.g. wrong Jim — meant to tap the other one"
        onConfirm={performUndo}
        onClose={() => setConfirmUndo(false)}
      />
    </li>
  );
}
